import React, { 
  useState, useEffect, useMemo, forwardRef, useContext 
} from 'react';
import PropTypes from 'prop-types';
import { Form } from 'components/atoms';
import { Formik, Field } from 'formik';
import {
  FieldMarkdown,
  FieldCustom,
  FieldArrayDndFormik,
  FieldToggle,
} from 'components/molecules';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom';
import { useFormikForwardRef } from 'hooks';
import { generateKey } from 'services/generators';
import { useDebounce } from 'react-use';
import { useUserContext } from 'context/UserContext';
import styled, { ThemeContext } from 'styled-components';

const MultipleDataSchema = Yup.object().shape({
  id: Yup.number(),
  title: Yup.string()
    .required('Campo obrigatório')
    .max(140, 'Must have less than 140 letters'),
  content: Yup.array()
    .of(
      Yup.object().shape({
        name: Yup.string()
          .required('Campo obrigatório')
          .max(140, 'Must have less than 140 letters'),
        text: Yup.string().required('Campo obrigatório'),
      })
    )
    .required('Campo obrigatório'),
});

const Schema = Yup.object().shape({
  name: Yup.string()
    .required('Campo obrigatório')
    .max(140, 'Must have less than 140 letters'),
  text: Yup.string().required('Campo obrigatório'),
});

const AlignedFieldToogle = styled(FieldToggle)`
  > div {
    align-items: center;
  }
`;

// eslint-disable-next-line react/prop-types
const FAQItemForm = forwardRef(({ initialValues, onSubmit }, ref) => {
  // this is necessary for all FieldArrayDnd forms
  // in order to control the submitForm from parent
  const formRef = useFormikForwardRef(ref);

  const initialValuesComputed = useMemo(() => {
    const { text, name } = initialValues;

    return {
      text: text ?? '',
      name: name ?? '',
    };
  }, [initialValues]);

  const formToModuleSchema = values => {
    const { text, name } = values;

    return {
      text,
      name,
    };
  };

  return (
    <Formik
      ref={formRef}
      initialValues={initialValuesComputed}
      validationSchema={Schema}
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(formToModuleSchema(values));
        setSubmitting(false);
      }}
      validateOnMount
      enableReinitialize
    >
      <Form>
        <Field
          title="Título"
          name="name"
          info="Título do destaque"
          maxLetters={140}
          component={FieldCustom}
        />
        <Field
          title="Descrição"
          name="text"
          component={FieldMarkdown}
          maxLetters={500}
        />
      </Form>
    </Formik>
  );
});

// eslint-disable-next-line react/prop-types
const FAQMultipleDataForm = forwardRef(({ initialValues, onSubmit }, ref) => {
  // this is necessary for all FieldArrayDnd forms
  // in order to control the submitForm from parent
  const formRef = useFormikForwardRef(ref);

  const initialValuesComputed = useMemo(() => {
    const { id, title, content } = initialValues;

    return {
      id,
      title: title ?? '',
      content: content ?? [],
    };
  }, [initialValues]);

  const formToModuleSchema = values => {
    const { id, title, content } = values;

    return {
      id,
      title,
      content,
    };
  };

  return (
    <Formik
      ref={formRef}
      initialValues={initialValuesComputed}
      validationSchema={MultipleDataSchema}
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(formToModuleSchema(values));
        setSubmitting(false);
      }}
      validateOnMount
      enableReinitialize
    >
      <Form>
        <Field
          title="Título"
          name="title"
          info="Título da categoria"
          component={FieldCustom}
        />
        <Field
          name="content"
          component={FieldArrayDndFormik}
          columns={[
            {
              accessor: 'name',
              Header: 'Conteúdo',
            },
          ]}
          rowKey={item => generateKey(item.id)}
          ItemForm={FAQItemForm}
        />
      </Form>
    </Formik>
  );
});

const FaqEditor = ({ initialProps, onChange }) => {

  const [{
    user
  }] = useUserContext();
  const {
    subweb
  } = useParams()
  const themeContext = useContext(ThemeContext)
  const subwebColor = useMemo(() => (
    user.subwebs.find(sub => (sub.slug === subweb))?.color ?? themeContext.colors.black
  ), [])

  const [isLoading, setLoading] = useState(true);
  const [editorProps, setEditorProps] = useState(initialProps);
  const { lang } = useParams();

  const initialValues = useMemo(() => {
    const { 
      title, data, multipleData, hasMultiple 
    } = initialProps;


    return {
      title,
      data,
      multipleData,
      hasMultiple,
      activeColor: subwebColor
    };
  }, [initialProps]);

  useEffect(() => {
    setLoading(false);
  }, [lang]);

  const handleFormChange = newValues => {
    const { 
      data, hasMultiple, title, multipleData, activeColor
    } = newValues;

    const newProps = {
      ...initialProps,
      activeColor,
      data,
      hasMultiple,
      title,
      multipleData: multipleData.map(t => {
        if (t.id === undefined) return { ...t, id: multipleData.length };
        return t;
      }),
    };

    setEditorProps(newProps);
  };

  useDebounce(
    () => {
      onChange(editorProps);
    },
    200,
    [editorProps]
  );

  if (isLoading) {
    return <div>A carregar...</div>;
  }

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={Schema}
        validate={handleFormChange}
        enableReinitialize
      >
        {({ values }) => (
          <Form>
            <Field
              title="Título"
              name="title"
              info="Título do módulo de Perguntas frequentes"
              component={FieldCustom}
              maxLetters={120}
            />
            <Field
              title="Categorias"
              name="hasMultiple"
              component={AlignedFieldToogle}
            />
            {values.hasMultiple === false && (
              <Field
                name="data"
                component={FieldArrayDndFormik}
                columns={[
                  {
                    accessor: 'name',
                    Header: 'Título',
                  },
                ]}
                rowKey={item => generateKey(item.id)}
                ItemForm={FAQItemForm}
              />
            )}
            {values.hasMultiple === true && (
              <Field
                name="multipleData"
                component={FieldArrayDndFormik}
                columns={[
                  {
                    accessor: 'title',
                    Header: 'Categorias',
                  },
                ]}
                rowKey={item => generateKey(item.id)}
                ItemForm={FAQMultipleDataForm}
              />
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

FaqEditor.propTypes = {
  onChange: PropTypes.func.isRequired,
  initialProps: PropTypes.shape({}),
};
FaqEditor.defaultProps = {
  initialProps: {},
};

export default FaqEditor;
